import {
  ChangeEvent,
  ReactNode,
  RefCallback,
  RefObject,
  type JSX,
} from 'react';
import { ButtonUses } from '../Button/Button.types';
import { OnChangeHandler } from '../../utils/useControllable';
import { Option, OptionGroup, OptionOrGroup, Options } from '../../types';
import {
  BaseStyleProps,
  DOMProps,
  TransitionalDataComponentProps,
} from '../../../types';
import { DisplayOptimizedContextType } from '../../context/DisplayOptimizedContext';
import { ButtonSizesShorthand } from '../Button/Button';
import { CSSObject } from 'foundations-theming/utils';

export type InternalOption = Option & {
  dropdownText?: ReactNode;
  input?: string;
  invalid?: boolean;
  isSpecial?: boolean;
  title?: string;
  value?: string;
  selectAll?: boolean;
  className?: string; // required for defaultOptionRenderer
  dropdownStyle?: string;
};

export type SelectValue =
  | (string | number | boolean)
  | ((string | number | boolean) | undefined | null)[]
  | null;

export type SelectProps = SelectHiddenProps &
  DOMProps &
  BaseStyleProps &
  DisplayOptimizedContextType &
  TransitionalDataComponentProps & {
    /**
     * Shows a prompt to create a new option when the user enters a query that
     * doesn't match any existing option.
     * @tags Behavior
     */
    allowCreate?: boolean;
    /**
     * Determines the type of component to use as the base for the select. The
     * `"input"` base renders as a searchable text input, while the `"button"`
     * base renders as a button. `"combined"` is a mix of both text input and
     * button features with an input-like affordance. `"auto"` defaults to
     * `"combined"` for multi-selects and `"button"` for single selects. `"input"`
     * cannot be used with multi-selects due to usability issues; use
     * `"combined"` instead.
     * @tags Appearance
     */
    anchorType?: 'auto' | 'button' | 'combined' | 'input';
    /**
     * Accessible label for the select
     * See [MDN](https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Reference/Attributes/aria-label).
     */
    'aria-label'?: string;
    /**
     * When the HTML `autoComplete` attribute is `"off"`, it prevents the
     * browser's own select from appearing over the component's dropdown. Works
     * with `anchorType="combined"` selects only.
     * See [MDN](https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/autocomplete).
     * @tags Form
     */
    autoComplete?: string;
    /**
     * Moves page focus to the component's reference element when the component
     * first mounts. This can be helpful on pages where the select component is
     * the core interactive element, like on search pages.
     * See [MDN](https://developer.mozilla.org/en-US/docs/Web/HTML/Global_attributes/autofocus).
     * @tags Behavior
     */
    autoFocus?: boolean;
    /**
     * Determines if a call to `loadOptions` should occur when the search string
     * is empty.
     * @tags Data
     */
    autoload?: boolean;
    /**
     * Provide a custom button component to render if `anchorType="button"`. The
     * component is passed a `buttonText` prop. Props do not need to be spread to this component.
     * @tags Customization
     */
    ButtonContent?: any;
    /**
     * Provide extra props to the `ButtonContent` component. This helps to avoid
     * need for declaring `ButtonContent` inside another component.
     * @tags Customization
     */
    buttonContentProps?: any;
    /**
     * Sets the size of the button used for the anchor of the component,
     * corresponding to `UIButton` sizes. Only used when `anchorType="button"`.
     * @tags Appearance
     */
    buttonSize?: ButtonSizesShorthand;
    /**
     * Sets the visual styles of the button used for the anchor of the component,
     * corresponding to `UIButton` uses. Only used when `anchorType="button"`.
     * @tags Appearance
     */
    buttonUse?: ButtonUses;
    /**
     * Sets the cache object used for options. Alternatively, set to `false` if
     * you'd like to disable caching.
     * @tags Data
     */
    cache?: Object | boolean;
    /**
     * Provide a custom component to be used to render the caret that appears
     * inside the dropdown button. The component is passed the corresponding
     * `buttonUse` prop. Only used when `anchorType="button"`.
     * @tags Customization
     */
    caretRenderer?: Function | ReactNode;
    /**
     * Provides an affordance to clear the current value of the select. When used
     * with `anchorType="combined"` an "X" is shown on the input, otherwise the
     * placeholder value is added as an option to clear the select.
     * @tags Behavior
     */
    clearable?: boolean;
    /**
     * Automatically triggers a close event for the option menu if the select
     * anchor is scrolled out of view.
     * @tags Behavior
     */
    closeOnTargetLeave?: boolean;
    /**
     * A style object to be applied to the select.
     * @tags Customization
     */
    cssStyles?: CSSObject;
    /**
     * Test identifier applied to the anchor element for automated testing
     * @tags Testing
     */
    'data-test-id'?: string;
    /**
     * Determines whether the option menu is open by default. The `open` prop takes
     * precedence over this if used in a controlled manner.
     * @tags State
     */
    defaultOpen?: boolean;
    /**
     * The `value` to use for initial selection of an option.
     * @tags Form
     */
    defaultValue?: SelectValue;
    /**
     * When this character is included within a `defaultValue` or `value` string,
     * the value will split at the delimiter. For example,
     * `defaultValue="hey;there"` will render as two separate options, "hey" and
     * "there", in a multi-select.
     * @tags Form
     */
    delimiter?: string;
    /**
     * Determines if the select input is disabled.
     * See [MDN](https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/disabled).
     * @tags Form
     */
    disabled?: boolean;
    /**
     * Content that will be shown at the bottom of the select dropdown; most
     * commonly used for an "Add option" button that opens a popover with input
     * fields to gather more information for the option creation.
     * @tags Content
     */
    dropdownFooter?: ReactNode;
    /**
     * When `true`, shows an error affordance around the select input.
     * `UIFormControl` should be used around the select to add inline error
     * messaging.
     * @tags Form
     */
    error?: boolean;
    /**
     * Provides custom functionality to replace the method used to filter options.
     * Accepts a boolean or a callback function that returns a list of options.
     * Descriptions of these filtering options can be found in the
     * [HubSpot/react-select-plus docs](https://github.com/HubSpot/react-select-plus#filtering-options).
     * @tags Data
     */
    filterOptions?:
      | ((
          options: Array<any>,
          filterValue: string,
          excludeOptions: Array<any>
        ) => Array<any>)
      | boolean;
    /**
     * An id that is passed to the computed anchor element. Works with
     * `anchorType="button"` or `anchorType="combined"`.
     * See [MDN](https://developer.mozilla.org/en-US/docs/Web/HTML/Global_attributes/id).
     * @tags Accessibility
     */
    id?: string;
    /**
     * hidden prop: ask us about your use case in #hds-support
     * @tags Behavior
     */
    ignoreAccents?: boolean;
    /**
     * hidden prop: ask us about your use case in #hds-support
     * @tags Behavior
     */
    ignoreCase?: boolean;
    /**
     * Provide a custom input component to use as the anchor component. Only used
     * when `anchorType="input"`.
     * @tags Customization
     */
    inputComponent?: any;
    /**
     * Set a `ref` to the underlying input component.
     * @tags Customization
     */
    inputRef?: any;
    /**
     * Controls the value of the underlying input. Only used when
     * `anchorType="input"`.
     * @tags State
     */
    inputValue?: string;
    /**
     * Shows a loading spinner at the end of the options menu.
     * @tags State
     */
    isLoading?: boolean;
    /**
     * hidden prop: ask us about your use case in #hds-support
     * @tags Behavior
     */
    isOptionUnique?: Function;
    /**
     * The component used to render an option in both the dropdown list and the
     * selected value box. Receives the option object as a prop, along with
     * several other props that should get routed to the root element of the
     * `itemComponent`. The `optionComponent` prop overrides this prop's value
     * for rendering options in the dropdown list, and the `valueComponent` prop
     * overrides this prop's value for rendering options in the selected value box.
     * IMPORTANT: This component should be memoized with React.memo() to prevent unnecessary re-renders.
     * Failure to do so will result in poor runtime performance on selects with thousands of options, as common interactions such as hover require re-rendering all options
     * @tags Customization
     */
    itemComponent?: any;
    /**
     * Provide a custom component that prompts the user to load more options in a
     * paginated select.
     * @tags Customization
     */
    loadMoreComponent?: any | ReactNode;
    /**
     * Provide a custom function that asynchronously loads options.
     * @tags Data
     */
    loadOptions?: Function;
    /**
     * Provide a custom function to render the options menu, typically used for
     * virtualized menu rendering. Ordinarily set to `foundations-components/transitional/utils/virtualizedMenuRenderer`, a react-virtualized function which
     * accepts a number of named parameters.
     * @tags Customization
     */
    menuRenderer?: {
      (
        menuWidth: number,
        virtualizedMenuRendererRefCallback: RefCallback<any>,
        menuHeight?: number
      ): any;
      displayName: string;
    };
    /**
     * A style object to be applied to the options menu.
     * @tags Customization
     */
    menuStyles?: BaseStyleProps;

    /**
     * Sets a custom width on the dropdown menu.
     * @tags Positioning
     */
    menuWidth?: number | 'auto';
    /**
     * Sets a custom height on the dropdown menu.
     * @tags Positioning
     */
    menuHeight?: number;
    /**
     * The minimum number of options required before the search input appears in
     * the dropdown. If `searchable={false}`, the search input will not appear
     * regardless of the number of options.
     * @tags Behavior
     * @default 8
     */
    minimumSearchCount?: number;
    /**
     * Whether multiple options can be selected.
     * @tags Behavior
     */
    multi?: boolean;
    /**
     * Defines the max amount of selected options that can appear in the closed
     * state. Only works for `multi={true}`.
     * @tags Appearance
     */
    multiCollapseLimit?: number;
    /**
     * Text shown when there are no results for given search term within the
     * select.
     * @tags Content
     */
    noResultsText?: any;
    /**
     * Provides a custom function that is called whenever the value of the select
     * changes.
     * @tags Events
     */
    onChange?: Function;
    /**
     * Triggered whenever a key is pressed when there's focus on an open select
     * (including arrow keys, shift, etc).
     * @tags Events
     */
    onInputKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
    /**
     * Triggered whenever the search value has changed or an option has been
     * selected (even if it's the same option).
     * @tags Events
     */
    onInputChange?: (value: string) => void;
    /**
     * Triggered whenever the search value has changed.
     * @tags Events
     */
    onInputValueChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
    /**
     * A function to call whenever the select's menu open state changes.
     * @tags Events
     */
    onOpenChange?: OnChangeHandler<boolean> | undefined;
    /**
     * A function to call whenever a value is pasted into the select. The pasted
     * value can be accessed from the clipboardData field on the event object.
     * @tags Events
     */
    onPaste?: Function;
    /**
     * Triggered whenever an option is selected. This event fires even if the
     * same option is selected, unlike `onChange`.
     * @tags Events
     */
    onSelectedOptionChange?: Function;
    /**
     * Controls the open state of the option menu.
     * @tags State
     */
    open?: boolean;
    /**
     * The component used to render an option in the dropdown list. Receives the
     * option object as a prop, along with several other props that should get
     * routed to the root element of the `optionComponent`. Defaults to the value
     * of the `itemComponent` prop.
     * IMPORTANT: This component should be memoized with React.memo() to prevent unnecessary re-renders.
     * Failure to do so will result in poor runtime performance on selects with thousands of options, as common interactions such as hover require re-rendering all options
     * @tags Customization
     */
    optionComponent?: any;
    /**
     * Provide a custom function that is responsible for rendering each option.
     * Receives an option object as its arguments.
     * IMPORTANT: This function should be memoized with useCallback and return a memoized component (wrapped in React.memo()) to prevent unnecessary re-renders.
     * Failure to do so will result in poor runtime performance on selects with thousands of options, as common interactions such as hover require re-rendering all options
     * @tags Customization
     */
    optionRenderer?: Function;
    /**
     * When enabled, options that have a truthy `isSpecial` key are rendered
     * using the default option renderer instead of the passed optionRenderer
     * prop.
     * @tags Customization
     */
    optionRendererIgnoresSpecialOptions?: boolean;
    /**
     * The options from which the user may select from. In most cases, each
     * option object must have a `text` key, which controls the display text
     * and is used in search, and a `value` key.
     * @tags Data
     */
    options?: Options;
    /**
     * The placeholder text in the select. This also becomes the text that allows
     * a user to clear the current selected option when used with clearable
     * `anchorType="button"|"input"`.
     * @tags Form
     */
    placeholder?: ReactNode;
    /**
     * Controls the placement of the option menu popover.
     * @tags Positioning
     */
    placement?:
      | 'left'
      | 'right'
      | 'top'
      | 'bottom'
      | 'left top'
      | 'left bottom'
      | 'right top'
      | 'right bottom'
      | 'top left'
      | 'top right'
      | 'bottom left'
      | 'bottom right';
    /**
     * A function that returns custom text for prompting the user to create a new
     * option. The new option's `text` value is passed through as the function
     * argument. Only used with `allowCreate={true}`.
     * @tags Content
     */
    promptTextCreator?: (label: string) => string | JSX.Element;
    /**
     * Determines if the select input is read-only or editable.
     * See [MDN](https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/readonly).
     * @tags Form
     */
    readOnly?: boolean;
    /**
     * The value set when the user clears the select input. Only possible when
     * `clearable={true}`.
     * @tags Form
     */
    resetValue?: SelectValue;
    /**
     * The ARIA role attribute specifying the role of the `Select` component.
     * See [MDN](https://developer.mozilla.org/en-US/docs/Web/API/Element/role).
     * @tags Accessibility
     */
    role?: string;
    /**
     * Whether or not the search input is visible when there are enough options.
     * Note that in the current behavior, the select still always filters when typing in the input.
     * To force the search input to always be visible, even with a small
     * number of options, pass `minimumSearchCount={0}`.
     * @tags Behavior
     * @default true
     */
    searchable?: boolean;
    /**
     * Placeholder text in the search input within the select.
     * @tags Content
     */
    searchPlaceholder?: string;
    /**
     * Text to display when options have not been fetched yet by `loadOptions`.
     * @tags Content
     */
    searchPromptText?: any;
    /**
     * A regex string to define the delimiter(s) between options for
     * multi-selects.
     * @tags Form
     */
    splitRegex?: RegExp;
    /**
     * Inline styles are not supported. Use `cssStyles` prop instead.
     * @tags Customization
     */
    style?: never;
    /**
     * Sets a custom value for `tabIndex` on the rendered anchor element.
     * See [MDN](https://developer.mozilla.org/en-US/docs/Web/HTML/Global_attributes/tabindex).
     * @tags Accessibility
     */
    tabIndex?: number;
    /**
     * Controls the current value of the select. For `anchorType="input"`, use
     * `inputValue` instead.
     * @tags Form
     */
    value?: SelectValue;
    /**
     * The component used to render the options in the selected value box.
     * Receives the option object as a prop, along with several other props that
     * should get routed to the root element of the `valueComponent`. Defaults to
     * the value of the `itemComponent` prop.
     * @tags Customization
     */
    valueComponent?: any;
    /**
     * Provide a custom function that is responsible for rendering the current
     * value of the select, shown in the anchor input or button.
     * @tags Customization
     */
    valueRenderer?: Function;
    /**
     * Whether the select is open.
     * @tags State
     */
    isOpen?: boolean;
  };

/**
 * @internal
 * **Do not use.** Ask about your use case in #hds-support.
 */
type SelectHiddenProps = {
  /**
   * @internal Forces the placeholder to be shown even if there is a value.
   */
  _forcePlaceholder?: boolean;
  /**
   * @internal A function to call whenever the select's menu closes.
   */
  onClose?: (evt: unknown) => void;
  /**
   * @internal A function to call whenever the select's menu opens.
   */
  onOpen?: (evt: unknown) => void;
  /**
   * @internal A function to call to get the internal select instance.
   */
  getInternalSelect?: any;
  /**
   * @internal @deprecated Use `autoFocus` instead
   */
  autofocus?: boolean;
  /**
   * @internal Internal select instance reference
   */
  select?: any;
  /**
   * @internal Function to get the internal async select instance
   */
  getInternalAsyncSelect?: any;
};

export const isOptionGroup = (option: OptionOrGroup): option is OptionGroup =>
  'options' in option && !!option.options;

export type OptionsCache = Record<string, Option>;
export type PaginationCache = Record<string, LoadOptionsPagination>;

export type SelectState = {
  controlWidth: number;
  initiallyOpen: boolean | undefined;
  inputValue: string;
  isLoading: boolean;
  optionsCache: OptionsCache;
  paginationCache?: PaginationCache;
  prevOpen: boolean | undefined;
  resetOption: Option | null;
  target: Element | Text | null;
};

export type LoadOptionsPagination = {
  hasMore: boolean;
  length?: number;
  offset: string | number;
};

export type LoadOptionsCallback = (
  cache: PaginationCache | null,
  args?: {
    options: (Option | undefined | null)[];
    pagination?: LoadOptionsPagination;
  }
) => void;

export interface CreatableSelectInstance
  extends TransitionalDataComponentProps {
  inputValue: string;
}

export type ComputedAnchorType = 'button' | 'combined' | 'input';

// This type does not include all of the methods and properties of the
// SelectCore class, it is a subset of the functions on the class that we use
// in utilities that is typed for type safety in those utilities
export interface SelectComponentInstance {
  popoverRef: RefObject<HTMLDivElement | null>;
  props: SelectProps;
  state: SelectState;
  areOptionsToggleable: ({
    anchorType,
    multi,
  }: Pick<SelectProps, 'anchorType' | 'multi'>) => boolean;

  defaultValueRenderer: (option: Option) => ReactNode;

  dropdownContentRefCallback: RefCallback<HTMLDivElement>;
  getAnchorType: ({
    anchorType,
    multi,
  }: Pick<SelectProps, 'anchorType' | 'multi'>) => ComputedAnchorType;

  getMenuWidth: () => number | string;

  handleOptionToggle: (
    option: Option,
    evt: ChangeEvent<HTMLInputElement>
  ) => void;

  renderDropdownContent: (
    props: SelectProps & { children: ReactNode }
  ) => ReactNode;

  renderDropdownFooter: () => ReactNode;
  triggerOpen: () => void;
}
